import { Fragment } from "react";
import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LockIcon from "@mui/icons-material/Lock";
import { WarningLink } from "./monthlyExpenseWarnings";

const SEPARATOR = " · ";

interface CloseBannersProps {
  /** Set once the month is closed: "ปิดเดือน… เมื่อ … โดย …". */
  closedText: string | null;
  warningLinks: WarningLink[];
  warnedBranchCount: number;
  reviewPath: string;
}

/**
 * A closed month says when and by whom, and that nothing reopens; an open one lists the daily
 * documents the close will not count, branch by branch, each a link to that view of S3. It only
 * warns — the close is never blocked by it.
 */
export default function CloseBanners({
  closedText,
  warningLinks,
  warnedBranchCount,
  reviewPath,
}: Readonly<CloseBannersProps>) {
  if (closedText) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={1.25}
        sx={{
          px: 2,
          py: 1.25,
          borderRadius: 2,
          bgcolor: "gray.main",
          color: "common.white",
        }}
      >
        <LockIcon fontSize="small" />
        <Typography variant="body2">{closedText}</Typography>
      </Stack>
    );
  }
  if (warningLinks.length === 0) return null;

  return (
    <Alert
      severity="warning"
      action={
        <Link
          component={RouterLink}
          to={reviewPath}
          fontWeight={500}
          whiteSpace="nowrap"
        >
          ไปหน้าอนุมัติ ›
        </Link>
      }
    >
      <Typography variant="body2">
        <b>เอกสารรายวันยังไม่ครบ {warnedBranchCount} สาขา</b> —{" "}
        {warningLinks.map((warning, index) => (
          <Fragment key={warning.key}>
            {index > 0 && SEPARATOR}
            <Link component={RouterLink} to={warning.path} color="inherit">
              {warning.text}
            </Link>
          </Fragment>
        ))}
      </Typography>
      <Typography variant="caption">
        กลุ่ม "จากรายวัน" นับเฉพาะใบที่อนุมัติแล้ว
      </Typography>
    </Alert>
  );
}
